import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Plus, Search, Eye, Building } from 'lucide-react';
import { AsyncBoundary, ConfirmDialog, Drawer, Icon, useToast } from '@kollez/design-system';
import { usePlatformTenants, useSuspendTenant, useSetTenantAiCap, type PlatformTenantRow } from '@/lib/queries';
import { formatIndianDate, formatTime } from '@kollez/shared';

/**
 * /platform/tenants (F89b) — direct port of kollez/pages/superadmin/tenants.html.
 *   Header + Provision CTA · Filterbar (search + plan) · Table with AI-spend meter
 *   Drawer per tenant: dl + AI-cap field + View-as (reason-gated 5+ chars)
 *   + Suspend (type-to-confirm first word uppercase)
 *   Inline audit-log section under the table (last 24 hours)
 */
function health(t: PlatformTenantRow): { label: string; tone: string; sub: string } {
  const cap = t.subscription?.aiCap ?? 0;
  const spent = t.subscription?.aiSpentThisMonth ?? 0;
  // Step 85 — Tenant.status is UPPERCASE in the DB ('ACTIVE' | 'INACTIVE' | 'SUSPENDED'
  // | 'TRIAL' | 'CANCELLED') per Tenant.ts:196. The old lowercase check never matched, so
  // a suspended tenant fell through to the Healthy/Watch branch. Compare uppercase to match.
  if (t.status === 'SUSPENDED') return { label: 'Suspended', tone: 'badge-danger', sub: 'Paused by platform ops' };
  if (cap > 0 && spent >= cap) return { label: 'Action needed', tone: 'badge-danger', sub: 'AI cap reached · Nexie paused' };
  if (cap > 0 && spent / cap >= 0.8) return { label: 'Watch', tone: 'badge-warning', sub: 'AI cap 80%+ used this month' };
  if (t.subscription) return { label: 'Healthy', tone: 'badge-success', sub: 'On plan · nothing to do' };
  return { label: 'Watch', tone: 'badge-warning', sub: 'No active plan' };
}

export function PlatformTenantsPage() {
  const { data, isLoading, error, refetch } = usePlatformTenants();
  const toast = useToast();
  const suspend = useSuspendTenant();
  const setCap = useSetTenantAiCap();
  const [capReason, setCapReason] = useState('');

  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [plan, setPlan] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [aiCapDraft, setAiCapDraft] = useState<string>('');

  const rows = data?.tenants ?? [];
  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((t) => {
      const okQ = !ql || t.name.toLowerCase().includes(ql);
      const okPlan = !plan || t.subscription?.planName === plan;
      return okQ && okPlan;
    });
  }, [rows, q, plan]);

  const opened = openId ? rows.find((t) => t._id === openId) : null;
  const openedHealth = opened ? health(opened) : null;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Tenants</h1>
          <p className="subhead">
            {rows.length} groups and colleges · {rows.reduce((s, t) => s + (t.users ?? 0), 0).toLocaleString('en-IN')} people · all data in Mumbai (ap-south-1)
          </p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-primary" to="/platform/provision"><Plus size={14} />Provision tenant</Link>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <div className="input-group fb-search">
            <Search size={14} />
            <input className="input" placeholder="Tenant name" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select className="select" value={plan} onChange={(e) => setPlan(e.target.value)}>
            <option value="">Any plan</option>
            <option>Group</option>
            <option>College</option>
            <option>University</option>
            <option>Practice only</option>
          </select>
          <span className="fb-count">{shown.length} of {rows.length}</span>
        </div>
        <AsyncBoundary
          kind="table"
          inCard
          loading={isLoading}
          error={error}
          empty={!isLoading && shown.length === 0}
          emptyTitle="No tenants yet"
          emptyHint="Provision your first tenant to get started."
          onRetry={() => void refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th className="hide-mobile">Plan</th>
                  <th className="r hide-mobile">Colleges</th>
                  <th className="r hide-mobile">Users</th>
                  <th>AI spend this month</th>
                  <th className="hide-mobile">Health</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((t) => {
                  const cap = t.subscription?.aiCap ?? 0;
                  const spent = t.subscription?.aiSpentThisMonth ?? 0;
                  const pct = cap > 0 ? Math.min(100, Math.round((spent / cap) * 100)) : 0;
                  const barTone = pct >= 100 ? 'danger' : pct >= 80 ? 'warning' : '';
                  const h = health(t);
                  return (
                    <tr
                      key={t._id}
                      onClick={() => nav(`/platform/tenants/${t._id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="cell-title">{t.name}</div>
                        <div className="cell-sub">{h.sub}</div>
                      </td>
                      <td className="hide-mobile">{t.subscription?.planName ?? '—'}</td>
                      <td className="r hide-mobile">{t.colleges ?? '—'}</td>
                      <td className="r hide-mobile">{(t.users ?? 0).toLocaleString('en-IN')}</td>
                      <td>
                        <div className="grid gap-1" style={{ minWidth: 140 }}>
                          {cap > 0 ? (
                            <>
                              <div className="text-sm">
                                ₹{spent.toLocaleString('en-IN')} <span className="cell-sub">of ₹{cap.toLocaleString('en-IN')}</span>
                              </div>
                              <div className={`bar ${barTone}`}><span style={{ width: `${pct}%` }} /></div>
                            </>
                          ) : (
                            <span className="cell-sub">No AI cap set</span>
                          )}
                        </div>
                      </td>
                      <td className="hide-mobile"><span className={`badge ${h.tone}`}>{h.label}</span></td>
                      <td>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          aria-label="Quick actions"
                          onClick={(e) => { e.stopPropagation(); setOpenId(t._id); setAiCapDraft(String(cap)); }}
                        >
                          <Icon name="more-horizontal" size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      {/* Recent-activity block removed; belongs on /platform/audit which
          already shows the full stream with rich filters. Keeping it here
          just duplicated the same auth.login entries on every tenants
          reload with no cross-linking back to a specific tenant. */}

      <Drawer open={Boolean(opened)} onClose={() => setOpenId(null)} title={opened?.name ?? ''} size="lg">
        {opened && openedHealth && (
          <>
            <p className="eyebrow">{opened.subscription?.planName ?? 'No plan'} · {opened.colleges ?? '—'} colleges</p>
            <h2 className="h-section">{opened.name}</h2>
            <dl className="dl mt-4">
              <dt>Status</dt><dd><span className={`badge ${openedHealth.tone}`}>{openedHealth.label}</span> {openedHealth.sub}</dd>
              <dt>Users</dt><dd>{(opened.users ?? 0).toLocaleString('en-IN')}</dd>
              <dt>Billing</dt><dd>PO / GST invoice / NEFT</dd>
              <dt>Data region</dt><dd>Mumbai (ap-south-1)</dd>
            </dl>

            <div className="grid gap-2 mt-4">
              <label className="label" htmlFor="cap">Monthly AI cap</label>
              <div className="input-group">
                <span className="input-addon">₹</span>
                <input id="cap" className="input" value={aiCapDraft} onChange={(e) => setAiCapDraft(e.target.value)} />
              </div>
              <label className="label mt-2" htmlFor="cap-reason">Reason (min 5 chars) — recorded on AuditLog</label>
              <input id="cap-reason" className="input" value={capReason} onChange={(e) => setCapReason(e.target.value)} placeholder="Ticket #… / exam-season surge" />
            </div>

            <div className="grid gap-2 well p-4 mt-4">
              <div className="font-medium flex items-center gap-2"><Eye size={14} />View as a tenant user</div>
              <p className="helper">For support only. The tenant admin is emailed, and every page you open is logged.</p>
              <p className="helper m-0">Pick the specific user from the directory — impersonation is per-user, not per-tenant.</p>
              <Link className="btn btn-outline justify-self-start" to={`/platform/users?tenant=${opened._id}`}>
                <Eye size={14} />Open users of this tenant
              </Link>
            </div>

            <div className="drawer-foot">
              <button className="btn btn-danger" onClick={() => setSuspendOpen(true)}>
                <Building size={14} />Suspend tenant
              </button>
              <button
                className="btn btn-primary ml-auto"
                disabled={!aiCapDraft || Number(aiCapDraft) < 0 || capReason.trim().length < 5 || setCap.isPending}
                onClick={() => {
                  setCap.mutate(
                    { tenantId: opened._id, monthlyBudgetInr: Number(aiCapDraft), reason: capReason.trim() },
                    {
                      onSuccess: () => {
                        toast.show({ message: `AI cap saved for ${opened.name}.` });
                        setOpenId(null); setCapReason('');
                      },
                      onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
                    },
                  );
                }}
              >
                {setCap.isPending ? 'Saving…' : 'Save cap'}
              </button>
            </div>
          </>
        )}
      </Drawer>

      <ConfirmDialog
        open={suspendOpen}
        onOpenChange={setSuspendOpen}
        title={opened ? `Suspend ${opened.name}?` : 'Suspend tenant?'}
        description={opened ? `${(opened.users ?? 0).toLocaleString('en-IN')} people lose access immediately, including students with pending offers. Data is kept.` : ''}
        confirmText="Suspend"
        tone="danger"
        typeToConfirm={opened ? opened.name.split(' ')[0]!.toUpperCase() : undefined}
        onConfirm={async () => {
          if (!opened) return;
          try {
            await suspend.mutateAsync({ id: opened._id, typedConfirmation: opened.name.split(' ')[0]!.toUpperCase() });
            toast.show({ message: `${opened.name} suspended.` });
            setSuspendOpen(false);
            setOpenId(null);
          } catch {
            toast.show({ tone: 'warning', message: 'Suspend failed.' });
          }
        }}
      />
    </div>
  );
}
